import { defineMessages, useIntl, FormattedMessage } from 'dibs-react-intl';
import { getChainIdFromEnv } from 'dibs-crypto-utils/exports/supportedChains';
import { Link } from 'dibs-elements/exports/Link';

const messages = defineMessages({
    pendingLabel: {
        id: 'nft.pdp.priceHeader.pendingLabel',
        defaultMessage: 'Your Pending Bid',
    },
    soldLabel: {
        id: 'nft.pdp.priceHeader.soldLabel',
        defaultMessage: 'Sold Price',
    },
    soldTooltip: {
        id: 'nft.pdp.priceHeader.soldTooltip',
        defaultMessage: 'This is the price that the owner paid for this artwork.',
    },
    ownerLabel: {
        id: 'nft.pdp.priceHeader.ownerLabel',
        defaultMessage: 'You Paid',
    },
    ownerTooltip: {
        id: 'nft.pdp.priceHeader.ownerTooltip',
        defaultMessage: 'This is the price you paid for this artwork.',
    },
    currentBidLabel: {
        id: 'nft.pdp.priceHeader.currentBidLabel',
        defaultMessage: 'Current Bid',
    },
    currentBidTooltip: {
        id: 'nft.pdp.priceHeader.currentBidTooltip',
        defaultMessage:
            'This is the current highest bid. In order to own this item, you will need the highest bid at the end of the auction. {learnMoreLink}',
    },
    reservePriceLabel: {
        id: 'nft.pdp.priceHeader.reservePriceLabel',
        defaultMessage: 'Reserve Price',
    },
    reservePriceTooltip: {
        id: 'nft.pdp.priceHeader.reservePriceTooltip',
        defaultMessage:
            'This is the starting value the artist has set for this artwork. In order to own this item, you will need the highest bid at the end of the auction. {learnMoreLink}',
    },
    reservePriceUnsetTooltip: {
        id: 'nft.pdp.priceHeader.reservePriceUnsetTooltip',
        defaultMessage:
            'The artist has not yet set the starting value of this artwork. In order to own this item, you will need the highest bid at the end of the auction. {learnMoreLink}',
    },
});

const learnMoreHref =
    getChainIdFromEnv() === 1 ? '/info/nft-faq/' : 'https://wp-info.stage.1stdibs.com/nft-faq/';

export const learnMoreLink = (
    <Link href={learnMoreHref} key="nft.pdp.price.learnMore" target="_blank">
        <FormattedMessage defaultMessage="Learn more" id="nft.pdp.price.learnMore" />
    </Link>
);

export function usePriceLabel({
    isEnded,
    hasCurrentBid,
    hasPendingBid,
    isOwner,
}: {
    isEnded: boolean;
    hasCurrentBid: boolean;
    hasPendingBid: boolean;
    isOwner: boolean;
}): string {
    const intl = useIntl();
    if (hasPendingBid) {
        return intl.formatMessage(messages.pendingLabel);
    }
    if (isEnded) {
        if (isOwner) return intl.formatMessage(messages.ownerLabel);
        if (hasCurrentBid) return intl.formatMessage(messages.soldLabel);
    } else if (hasCurrentBid) return intl.formatMessage(messages.currentBidLabel);
    return intl.formatMessage(messages.reservePriceLabel);
}
export function useTooltip({
    isEnded,
    hasCurrentBid,
    hasReservePrice,
    isOwner,
}: {
    isEnded: boolean;
    hasCurrentBid: boolean;
    hasReservePrice: boolean;
    isOwner: boolean;
}): string {
    const intl = useIntl();
    if (isEnded) {
        if (isOwner) return intl.formatMessage(messages.ownerTooltip);
        if (hasCurrentBid) return intl.formatMessage(messages.soldTooltip, { learnMoreLink });
    } else if (hasCurrentBid)
        return intl.formatMessage(messages.currentBidTooltip, { learnMoreLink });
    else if (hasReservePrice)
        return intl.formatMessage(messages.reservePriceTooltip, { learnMoreLink });
    return intl.formatMessage(messages.reservePriceUnsetTooltip, { learnMoreLink });
}
